import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import Draggable from 'react-draggable';
import { motion } from 'framer-motion';

const SavingProgressIndicator = ({ sessionId, userName }) => {
  const getDefaultPosition = () => ({
    x: Math.max(20, window.innerWidth - 300),
    y: 20,
  });

  const [position, setPosition] = useState(() => getDefaultPosition());
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (position && position.x > window.innerWidth - 100) {
        setPosition((prev) => ({
          ...prev,
          x: Math.max(20, window.innerWidth - 300),
        }));
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position]);

  return (
    <Draggable
      position={position}
      onStart={() => setIsDragging(true)}
      onStop={(_, data) => {
        setIsDragging(false);
        setPosition({ x: data.x, y: data.y });
      }}
    >
      {/* 👇 This wrapper must be a plain DOM element */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 2000,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={{ opacity: 1, scale: isDragging ? 1.05 : 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <Paper
            elevation={10}
            sx={{
              p: 3,
              minWidth: '280px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #8f8ce7, #6b68db)',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              boxShadow:
                '0 8px 25px rgba(0,0,0,0.25), 0 0 15px rgba(147, 145, 239, 0.5)',
              animation: 'pulseGlow 2s ease-in-out infinite',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress
                size={46}
                thickness={5}
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  animation: 'spinSlow 1.8s linear infinite',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  opacity: 0.9,
                  animation: 'pulseDot 1.5s ease-in-out infinite',
                }}
              />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  mb: 0.5,
                }}
              >
                Saving Session Notes...
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '0.85rem',
                }}
              >
                {userName ? `For ${userName}` : 'Working in background'}
              </Typography>
            </Box>
          </Paper>
        </motion.div>

        {/* Inline keyframes for glow + pulse */}
        <style>
          {`
            @keyframes pulseGlow {
              0% { box-shadow: 0 8px 25px rgba(0,0,0,0.25), 0 0 15px rgba(147, 145, 239, 0.5); }
              50% { box-shadow: 0 8px 25px rgba(0,0,0,0.25), 0 0 25px rgba(147, 145, 239, 0.9); }
              100% { box-shadow: 0 8px 25px rgba(0,0,0,0.25), 0 0 15px rgba(147, 145, 239, 0.5); }
            }
            @keyframes spinSlow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pulseDot {
              0%, 100% { transform: scale(1); opacity: 0.8; }
              50% { transform: scale(1.4); opacity: 1; }
            }
          `}
        </style>
      </div>
    </Draggable>
  );
};

export default SavingProgressIndicator;
